import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Boxes } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PasswordInput } from "@/components/PasswordInput";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const u = await login(identifier, password);
      toast.success("Welcome back!");
      navigate(["admin", "staff"].includes(u.role) ? "/admin" : "/");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:block relative bg-slate-900 grain">
        <img src="https://images.unsplash.com/photo-1759674861540-afed9f86f94a?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200" alt="" className="absolute inset-0 h-full w-full object-cover opacity-25" />
        <div className="relative p-12 h-full flex flex-col justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src="/logo.png" alt="Zealous Ventures" className="h-12 w-auto bg-white rounded-lg p-2" />
          </Link>
          <div>
            <h2 className="font-heading text-3xl font-bold text-white leading-tight">Your wholesale desk,<br />always in sync.</h2>
            <p className="text-slate-300 mt-3 max-w-md">Live wholesale pricing and stock, tiered volume discounts and instant reordering.</p>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12 bg-slate-50">
        <div className="w-full max-w-sm">
          <h1 className="font-heading text-2xl font-bold text-slate-900">Sign in</h1>
          <p className="text-slate-500 text-sm mt-1 mb-6">Access your wholesale account.</p>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label htmlFor="identifier">Username <span className="text-slate-400 font-normal">(Email / Phone number)</span></Label>
              <Input id="identifier" data-testid="login-email" type="text" value={identifier} onChange={(e) => setIdentifier(e.target.value)} required className="mt-1.5 h-11" placeholder="you@example.com or +91 …" />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label htmlFor="password">Password</Label>
                <Link to="/forgot-password" className="text-xs font-medium text-amber-600 hover:underline" data-testid="forgot-password-link">Forgot password?</Link>
              </div>
              <PasswordInput id="password" data-testid="login-password" value={password} onChange={(e) => setPassword(e.target.value)} required wrapperClassName="mt-1.5" className="h-11" />
            </div>
            {error && <div className="text-sm text-rose-600" data-testid="login-error">{error}</div>}
            <Button type="submit" disabled={loading} className="w-full h-11 rounded-full bg-slate-900 hover:bg-black text-white" data-testid="login-submit">
              {loading ? "Signing in..." : "Sign in"}
            </Button>
          </form>
          <p className="text-sm text-slate-500 mt-6 text-center">
            New buyer?{" "}
            <Link to="/register" className="text-amber-600 font-medium hover:underline">Register your business</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
